import { keyBy } from 'lodash'
import { useMemo } from 'react'

import {
  selectBillingEndDateAsDate,
  selectBillingStartDateAsDate,
  selectBusinessLine,
  selectEndDateAsDate,
  selectPurchaseOrderNumber,
  selectBillingName,
  selectStartDateAsDate,
  selectServicePrice,
  selectServiceType,
  selectMeasure,
  selectMaterial,
  selectServiceMethod,
  selectServiceAction,
  selectPricingZone,
  selectPricingPeriodPer,
  selectPricingPeriodInterval,
  selectRecurrenceInterval,
  selectRecurrencePer,
  selectRecurrenceFrequency,
  selectRecurrence as selectRecurrenceObj,
  selectSortedFormattedServiceEvents,
  selectTaxZone,
  selectDayOfTheWeek,
  selectDayOfTheMonth,
  selectEventOccurrence,
  selectDaysOfWeekFilledForDisplay,
  selectIsServiceDeactivated,
  selectRecurringServiceEvents,
  selectDeactivateOnEndDate,
} from 'data/configured-service/configuredServiceDetailsSelectors'
import {
  selectMeasures,
  selectMethods,
  selectMaterials,
  selectServiceActions,
  selectPricingZones,
  selectPricingPeriods,
  selectRecurrenceOptions,
  selectActiveBusinessLineOptions,
  selectTaxZoneOptions,
} from 'data/meta/metaSelectors'
import { EVENT_INSTANCE_TYPES, RECURRENCE, RECURRENCE_V2 } from 'settings/constants/service'
import { createDateFromString } from 'utils/date'
import { isWeeklyRecurrence as getIsWeeklyRecurrence } from 'utils/service'

type MetaOption = {
  id?: string | number | null
  label?: string | null
  default?: boolean | null
  periodPer?: string | null
  periodInterval?: string | null
  recurrenceInterval?: string | null
  recurrencePer?: string | null
}

const getEventInstanceType = (isRecurring: boolean, dayOfTheWeek: string | string[] | null | undefined) => {
  if (isRecurring === false) return null
  // Handle both single values and arrays
  const firstDayOfWeek = Array.isArray(dayOfTheWeek) ? dayOfTheWeek[0] : dayOfTheWeek
  if (firstDayOfWeek) return EVENT_INSTANCE_TYPES.DAY_OF_WEEK
  return EVENT_INSTANCE_TYPES.SPECIFIC_DATE
}

export const useServiceDetailsFormDefaults = (data: unknown, metaFiltersData: unknown) => {
  const measureList = useMemo(() => selectMeasures(metaFiltersData) as MetaOption[], [metaFiltersData])
  const methodList = useMemo(() => selectMethods(metaFiltersData) as MetaOption[], [metaFiltersData])
  const materialList = useMemo(() => selectMaterials(metaFiltersData) as MetaOption[], [metaFiltersData])
  const actionList = useMemo(() => selectServiceActions(metaFiltersData) as MetaOption[], [metaFiltersData])
  const pricingPeriods = useMemo(() => selectPricingPeriods(metaFiltersData) as MetaOption[], [metaFiltersData])
  const pricingZoneList = useMemo(() => selectPricingZones(metaFiltersData) as MetaOption[], [metaFiltersData])
  const businessLineList = useMemo(() => selectActiveBusinessLineOptions(metaFiltersData) as MetaOption[], [metaFiltersData])
  const recurrenceList = useMemo(() => selectRecurrenceOptions(metaFiltersData) as MetaOption[], [metaFiltersData])
  // Instance pricing is allowed for recurring services — it enables event-based (per-occurrence) pricing.
  // Keeping Instance here also lets an Instance-priced service resolve its current period below (it was
  // previously filtered out, leaving the field blank and silently nulling the period on save).
  const pricingPeriodList = pricingPeriods
  const taxZoneOptionList = useMemo(() => selectTaxZoneOptions(metaFiltersData) as MetaOption[], [metaFiltersData])

  const startDate = useMemo(() => selectStartDateAsDate(data), [data])
  const endDate = useMemo(() => selectEndDateAsDate(data), [data])
  const billingStartDate = useMemo(() => selectBillingStartDateAsDate(data), [data])
  const billingEndDate = useMemo(() => selectBillingEndDateAsDate(data), [data])
  const purchaseOrderNumber = useMemo(() => selectPurchaseOrderNumber(data), [data])
  const billingName = useMemo(() => selectBillingName(data), [data])
  const daysOfWeekData = useMemo(() => selectDaysOfWeekFilledForDisplay(data), [data])
  const price = useMemo(() => selectServicePrice(data), [data])
  const serviceType = useMemo(() => selectServiceType(data), [data])
  const businessLine = useMemo(() => selectBusinessLine(data), [data])
  const recurrenceInterval = useMemo(() => selectRecurrenceInterval(data), [data])
  const recurrencePer = useMemo(() => selectRecurrencePer(data), [data])
  const recurrenceFrequency = useMemo(() => selectRecurrenceFrequency(data), [data])
  const recurrenceObj = useMemo(() => selectRecurrenceObj(data), [data])
  const sortedFormattedServiceEvents = useMemo(() => selectSortedFormattedServiceEvents(data), [data])
  const isRecurringService = useMemo(() => serviceType === RECURRENCE_V2.RECURRING || serviceType === RECURRENCE.RECURRING, [serviceType])
  const isOnRequestService = useMemo(() => serviceType === RECURRENCE_V2.ON_REQUEST || serviceType === RECURRENCE.ON_REQUEST, [serviceType])
  const isWeeklyRecurrence = useMemo(() => getIsWeeklyRecurrence(recurrenceInterval), [recurrenceInterval])

  const measureObj = useMemo(() => selectMeasure(data), [data])
  const materialObj = useMemo(() => selectMaterial(data), [data])
  const methodObj = useMemo(() => selectServiceMethod(data), [data])
  const actionObj = useMemo(() => selectServiceAction(data), [data])
  const pricingZoneObj = useMemo(() => selectPricingZone(data), [data])
  const taxZoneObj = useMemo(() => selectTaxZone(data), [data])

  const periodPer = useMemo(() => selectPricingPeriodPer(data), [data])
  const periodInterval = useMemo(() => selectPricingPeriodInterval(data), [data])
  const dayOfTheWeek = useMemo(() => selectDayOfTheWeek(data), [data])
  const dayOfTheMonth = useMemo(() => selectDayOfTheMonth(data), [data])
  const eventOccurrence = useMemo(() => selectEventOccurrence(data), [data])
  const deactivateOnEndDate = useMemo(() => selectDeactivateOnEndDate(data), [data])
  const deactivate = useMemo(() => selectIsServiceDeactivated(data), [data])
  const eventInstanceType = useMemo(() => {
    return getEventInstanceType(isRecurringService, dayOfTheWeek)
  }, [data, isRecurringService, dayOfTheWeek])
  const daysOfWeek = useMemo<Record<string, unknown>>(() => {
    if (!daysOfWeekData || !daysOfWeekData.length) return {}

    return daysOfWeekData.reduce<Record<string, unknown>>((acc, dayOfWeekObj) => {
      const { day } = dayOfWeekObj
      acc[day] = dayOfWeekObj
      return acc
    }, {})
  }, [daysOfWeekData])
  const actionsById = useMemo(() => keyBy(actionList, 'id'), [actionList])
  const action = actionObj?.id ? actionList.find(item => item.id === actionObj.id) : null
  const method = methodObj?.id ? methodList.find(item => item.id === methodObj.id) : null
  const material = materialObj?.id ? materialList.find(item => item.id === materialObj.id) : null
  const measure = measureObj?.id ? measureList.find(item => item.id === measureObj.id) : null
  const pricingZone = pricingZoneObj?.id ? pricingZoneList.find(item => item.id === pricingZoneObj.id) : null
  const pricingPeriod = pricingPeriodList.find(item => item.periodPer === periodPer && item.periodInterval === periodInterval)
  const businessLineObj = businessLineList.find(item => item.label === businessLine)
  const selectedRecurrence = recurrenceObj && recurrenceObj.id ? recurrenceList.find(item => item.id === recurrenceObj.id) : null
  const selectedTaxZone = taxZoneObj && taxZoneObj.id ? taxZoneOptionList.find(item => item.id === taxZoneObj.id) : null
  const recurringServiceEvents = useMemo(() => selectRecurringServiceEvents(data), [data])

  const defaultRecurrence = useMemo(() => {
    if (selectedRecurrence || !isRecurringService || recurrenceList.length === 0) {
      return selectedRecurrence
    }

    const matchingRecurrence = recurrenceList.find(
      option => option.recurrenceInterval === recurrenceInterval && option.recurrencePer === recurrencePer
    )

    if (matchingRecurrence) {
      return matchingRecurrence
    }

    return recurrenceList[0]
  }, [selectedRecurrence, isRecurringService, recurrenceList, recurrenceInterval, recurrencePer])

  // Format service events for the form
  const formattedServiceEvents = useMemo(() => {
    if (!isOnRequestService || !sortedFormattedServiceEvents || sortedFormattedServiceEvents.length === 0) {
      return []
    }
    return sortedFormattedServiceEvents.map(event => ({
      id: event.id,
      serviceDate: createDateFromString(event.serviceDate),
      actionId: event.actionId,
      action: actionsById[event.actionId],
    }))
  }, [isOnRequestService, sortedFormattedServiceEvents, actionsById])

  const getReadyToPublish = () => {
    if (!isRecurringService) {
      return isOnRequestService
    }

    if (isWeeklyRecurrence) {
      return startDate && recurrenceFrequency === daysOfWeek.length
    }

    return startDate && recurringServiceEvents.length > 0
  }

  const readyToPublish = useMemo(
    () => getReadyToPublish(),
    [startDate, isRecurringService, isWeeklyRecurrence, recurringServiceEvents, daysOfWeek, recurrenceFrequency]
  )

  const datesMatch =
    startDate &&
    billingStartDate &&
    endDate &&
    billingEndDate &&
    startDate.getTime() === billingStartDate.getTime() &&
    endDate.getTime() === billingEndDate.getTime()

  const hasDifferentBillingDates = (billingStartDate || billingEndDate) && !datesMatch
  return useMemo(
    () => ({
      endDate,
      billingStartDate,
      billingEndDate,
      purchaseOrderNumber,
      billingName,
      useBillingName: Boolean(billingName),
      daysOfWeek: daysOfWeek || {},
      price: price ? price.toString() : null,
      material: material || null,
      method: method || null,
      action: action || null,
      measure: measure || null,
      pricingPeriod: pricingPeriod || null,
      pricingZone: pricingZone || null,
      serviceType: serviceType || RECURRENCE_V2.RECURRING,
      businessLine: businessLineObj || null,
      recurrenceInterval: recurrenceInterval || '',
      recurrencePer: recurrencePer || '',
      recurrenceFrequency: recurrenceFrequency || '1',
      recurrence: defaultRecurrence || null,
      serviceEvents: formattedServiceEvents,
      useDifferentDatesForBilling: hasDifferentBillingDates,
      deactivateOnEndDate,
      deactivate,
      eventInstanceType,
      eventOccurrence,
      dayOfTheWeek,
      dayOfTheMonth,
      startDate,
      taxZone: selectedTaxZone || null,
      recurringServiceEvents,
      readyToPublish,
    }),
    [
      endDate,
      billingStartDate,
      billingEndDate,
      purchaseOrderNumber,
      billingName,
      daysOfWeek,
      price,
      material,
      method,
      action,
      measure,
      pricingPeriod,
      pricingZone,
      serviceType,
      businessLineObj,
      recurrenceInterval,
      recurrencePer,
      recurrenceFrequency,
      defaultRecurrence,
      formattedServiceEvents,
      hasDifferentBillingDates,
      deactivateOnEndDate,
      deactivate,
      eventInstanceType,
      eventOccurrence,
      dayOfTheWeek,
      dayOfTheMonth,
      startDate,
      selectedTaxZone,
      recurringServiceEvents,
      readyToPublish,
    ]
  )
}
